import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStore } from 'react-redux';
import { setActiveTab, setLibraryFocusTrack } from '../redux/slices/appSlice';
import { openUnifiedResults, openMixpediaLabel } from '../redux/thunk/appNavThunk';
import {
    setActiveSearch,
    setArtistQuery,
    setTrackQuery,
    setMixQuery,
    setSearchAllQuery,
    setMixFilterContext,
    clearResults,
    selectMixpediaViewIsEmpty,
} from '../redux/slices/djpalSlice';
import {
    searchArtist,
    searchTrack,
    searchMixes,
    ensureToken,
    runTrackTabSearch,
} from '../redux/thunk/djpalThunk';
import {
    fetchSearchHistory,
    addSearchHistoryEntry,
    deleteSearchHistoryEntry,
    clearSearchHistoryBySurface,
} from '../redux/thunk/searchHistoryThunk';
import { selectSurfaceLoaded } from '../redux/slices/searchHistorySlice';
import { MIN_SEARCH_CHARS } from '../utils/searchConfig';
import { orderSections, eventKindLabel, eventSectionHeader, normalizeEventKind } from '../utils/searchSections';
import { eventFilters, eventFilterContext, eventHistoryEntry, eventFromHistory } from '../utils/eventNav';
import { resolveMixCover, useNativeMixCoverMode } from '../utils/nativeMixCover';
import { entityId } from '../utils/entityId';
import { buildMixOpenFilters } from '../utils/mixNavigation';
import { track as trackEvent } from '../utils/analytics';
import CoverArt from './CoverArt';
import { useDesignV2 } from '../utils/designV2';
import MobileSearchOverlay from './MobileSearchOverlay';
import './NavbarSearch.scss';

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const matchMobile = () => typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia(MOBILE_MEDIA_QUERY).matches;

// Flatten grouped results into a single indexable list for keyboard
// navigation: "See all" → In Your Library → the four catalog sections in
// `order` (computed once per fetch by orderSections — best-match score per
// section, see utils/searchSections). The leading "all" entry routes to the
// unified results page (/search?q=…) so it stays discoverable even when
// users always click the first suggestion. Library hits come first among
// results: an owned exact match beats a catalog match.
const buildFlatList = ({ library, artists, tracks, mixes, events = [], labels = [] }, query, order) => {
    const list = [];
    if (query && query.trim().length >= MIN_SEARCH_CHARS) list.push({ type: 'all', query: query.trim() });
    (library || []).forEach(f => list.push({ type: 'library', item: f }));
    const push = {
        events: () => events.forEach(ev => list.push({ type: 'event', item: ev })),
        artists: () => artists.forEach(a => list.push({ type: 'artist', item: a })),
        tracks: () => tracks.forEach(t => list.push({ type: 'track', item: t })),
        mixes: () => mixes.forEach(m => list.push({ type: 'mix', item: m })),
        labels: () => labels.forEach(l => list.push({ type: 'label', item: l })),
    };
    order.forEach(k => push[k] && push[k]());
    return list;
};

const NavbarSearch = () => {
    useNativeMixCoverMode(); // re-render when the native cover flag flips
    const [query, setQuery] = useState('');
    const [groups, setGroups] = useState({ library: [], artists: [], tracks: [], mixes: [], events: [], labels: [] });
    // Catalog-section display order, computed alongside `flat` at fetch time
    // (never at render time — the live query can drift from the one the flat
    // list was built with, and a re-derived order would desync keyboard nav).
    const [sectionOrder, setSectionOrder] = useState(['artists', 'events', 'tracks', 'mixes', 'labels']);
    const [flat, setFlat] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(-1);
    const [isMobile, setIsMobile] = useState(matchMobile);
    const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
    const wrapperRef = useRef(null);
    const debounceRef = useRef(null);
    const fetchSeqRef = useRef(0);

    // Track viewport so rotation between portrait/landscape doesn't strand
    // the user in the wrong mode.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
        const handler = (e) => setIsMobile(e.matches);
        // matchMedia.addEventListener is the modern API; addListener is the
        // deprecated one (still needed for older Safari).
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else if (mql.addListener) mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else if (mql.removeListener) mql.removeListener(handler);
        };
    }, []);
    const dispatch = useDispatch();
    const store = useStore();
    const allHistoryItems = useSelector((state) => state.searchHistory.items);
    const historyItems = React.useMemo(
        () => allHistoryItems.filter(e => (e.surface || 'mixpedia') === 'mixpedia'),
        [allHistoryItems]
    );
    const historyLoaded = useSelector(selectSurfaceLoaded('mixpedia'));

    // Show history when the input is focused AND empty — mirrors Spotify/SC.
    const showHistory = open && query.trim().length < MIN_SEARCH_CHARS && historyItems.length > 0;

    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Load history once so the first focus already has data to show.
    useEffect(() => {
        if (!historyLoaded) dispatch(fetchSearchHistory({ surface: 'mixpedia', limit: 10 }));
    }, [dispatch, historyLoaded]);

    // External clear (Mixpedia hub card resets to the home landing) — empty
    // the retained query text without needing a ref into this component.
    useEffect(() => {
        const handler = () => setQuery('');
        window.addEventListener('navbar-search-clear', handler);
        return () => window.removeEventListener('navbar-search-clear', handler);
    }, []);


    const fetchSuggestions = useCallback(async (q) => {
        if (q.length < MIN_SEARCH_CHARS) { setGroups({ library: [], artists: [], tracks: [], mixes: [], events: [], labels: [] }); setFlat([]); return; }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        try {
            // Local library search (desktop only — the web/capacitor shims have
            // no `db` namespace) runs alongside the Mixpedia call. Each side
            // degrades independently: a Mixpedia outage still shows library
            // hits, and vice versa.
            const localPromise = window.electron?.db?.tracks?.search
                ? window.electron.db.tracks.search({ searchTerm: q, limit: 5 }).catch(() => [])
                : Promise.resolve([]);
            let remote = {};
            try {
                const token = await ensureToken(dispatch, store.getState);
                const result = await window.electron.djpal.autocompleteUnified(token, q);
                if (result && !result.error) remote = result;
            } catch { /* library-only results still render */ }
            const library = await localPromise;
            if (seq !== fetchSeqRef.current) return;
            // The local DB also caches cloud/streaming rows (online://,
            // http(s):// — e.g. Apple previews). The Library tab is a
            // local-file manager and hides those, so offering them here would
            // be a click with nowhere to land.
            const isLocalFile = (f) => !/^(https?:|online:)/i.test(f.filePath || f.file || '');
            const next = {
                library: Array.isArray(library) ? library.filter(isLocalFile) : [],
                artists: remote.artists || [],
                events: remote.events || [],
                tracks: remote.tracks || [],
                mixes: remote.mixes || [],
                // Servers that predate label search send no `labels`.
                labels: remote.labels || [],
            };
            const nextOrder = orderSections(next, q);
            const nextFlat = buildFlatList(next, q, nextOrder);
            setGroups(next);
            setSectionOrder(nextOrder);
            setFlat(nextFlat);
            setSelectedIdx(-1);
            setOpen(nextFlat.length > 0);
        } catch {
            if (seq === fetchSeqRef.current) {
                setGroups({ library: [], artists: [], tracks: [], mixes: [], events: [], labels: [] });
                setFlat([]);
            }
        } finally {
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, [dispatch, store]);

    const handleChange = (val) => {
        setQuery(val);
        clearTimeout(debounceRef.current);
        // Switching between history view and autocomplete invalidates the
        // current keyboard selection — reset so the user starts at the top.
        setSelectedIdx(-1);
        if (val.trim().length < MIN_SEARCH_CHARS) {
            // Invalidate any slow in-flight fetch and clear the spinner so it
            // can't keep spinning (or repopulate stale results) after the user
            // deletes back below the threshold.
            fetchSeqRef.current++;
            setGroups({ library: [], artists: [], tracks: [], mixes: [], events: [], labels: [] });
            setFlat([]);
            setLoading(false);
            // Keep dropdown open so recent-searches view is visible when the
            // input goes back to empty; fall back to closed if no history.
            setOpen(historyItems.length > 0);
            return;
        }
        debounceRef.current = setTimeout(() => fetchSuggestions(val.trim()), 300);
    };

    // External "search for this" (Automix's empty filter hands its words over):
    // fill the query, open the dropdown and focus the field, as if typed.
    const handleChangeRef = useRef(handleChange);
    handleChangeRef.current = handleChange;
    useEffect(() => {
        const handler = (e) => {
            const q = String(e.detail?.query || '');
            handleChangeRef.current(q);
            setOpen(true);
            wrapperRef.current?.querySelector('input')?.focus();
        };
        window.addEventListener('navbar-search-set', handler);
        return () => window.removeEventListener('navbar-search-set', handler);
    }, []);

    // Filled home icon when Mixpedia home is the visible view.
    const homeActive = useSelector((state) => state.app.activeTab === 'djpal' && selectMixpediaViewIsEmpty(state));
    const designV2 = useDesignV2();
    const homeIconPx = designV2 ? 23 : 20;

    // Home button + the input's clear ×: close whatever Mixpedia page is
    // open and land on the home rows (Spotify's model — clearing search
    // returns you to browse). Page state FIRST, tab switch LAST: the
    // nav-history middleware snapshots djpal state when setActiveTab fires.
    const goMixpediaHome = () => {
        window.electron.analytics.track('navbar_home_click', {});
        dispatch(clearResults());
        commit('');
        dispatch(setActiveTab('djpal'));
    };

    // Commit a search/navigation. Keeps the committed text in the input
    // (Spotify-style) so the active query stays visible and editable.
    // Pass '' (the clear button does) to empty the box.
    const commit = (nextText = '') => {
        clearTimeout(debounceRef.current);
        fetchSeqRef.current++;
        setOpen(false);
        setQuery(nextText);
        setGroups({ library: [], artists: [], tracks: [], mixes: [], events: [], labels: [] });
        setFlat([]);
        setLoading(false);
    };

    const navigateToArtist = (artist) => {
        const name = typeof artist === 'string' ? artist : artist.name;
        const artistId = typeof artist === 'object'
            ? entityId(artist)
            : null;
        window.electron.analytics.track('navbar_search_select', { type: 'artist', name });
        trackEvent('search_result_clicked', { result_type: 'artist', name });
        // Page state FIRST, tab switch LAST: the nav-history middleware
        // snapshots djpal state when setActiveTab fires — dispatching it
        // first stored a stale snapshot, so Back landed on the wrong page.
        dispatch(setActiveSearch('artist'));
        dispatch(setArtistQuery(name));
        dispatch(setActiveTab('djpal'));
        dispatch(searchArtist({ artistName: name, artistId: artistId || null, artworkUrl: (typeof artist === 'object' && artist.artworkUrl) || null }));
        dispatch(addSearchHistoryEntry({
            entityType: 'artist',
            entityPublicId: artistId || `name:${name}`,
            entityName: name,
            entityArtwork: (typeof artist === 'object' && artist.artworkUrl) || null,
            surface: 'mixpedia',
        }));
        commit(name);
    };

    // Label suggestion → the label page. openMixpediaLabel owns the tab
    // switch and the nav-entry push (keep in sync with useUnifiedSearch).
    const navigateToLabel = (label) => {
        const publicId = label?.publicId || label?.id;
        if (!publicId) return;
        trackEvent('search_result_clicked', { result_type: 'label', name: label.name });
        dispatch(openMixpediaLabel({ publicId, name: label.name, from: 'search' }));
        dispatch(addSearchHistoryEntry({
            entityType: 'label',
            entityPublicId: publicId,
            entityName: label.name,
            entitySubtitle: label.trackCount ? `${label.trackCount} tracks` : 'Label',
            entityArtwork: label.artworkUrl || null,
            surface: 'mixpedia',
        }));
        commit(label.name);
    };

    const navigateToTrack = (track) => {
        const q = track.artist ? `${track.artist} - ${track.title}` : track.title;
        window.electron.analytics.track('navbar_search_select', { type: 'track', name: q });
        trackEvent('search_result_clicked', { result_type: 'track', name: q });
        dispatch(setActiveSearch('track'));
        dispatch(setTrackQuery(q));
        dispatch(setActiveTab('djpal'));
        dispatch(searchTrack({ trackName: q, trackId: entityId(track), artworkUrl: track.artworkUrl || null }));
        dispatch(addSearchHistoryEntry({
            entityType: 'track',
            entityPublicId: entityId(track) || `q:${q}`,
            entityName: track.title,
            entitySubtitle: track.artist || null,
            entityArtwork: track.artworkUrl || null,
            surface: 'mixpedia',
        }));
        commit(q);
    };

    const navigateToLibraryTrack = (file) => {
        const t = file.track || {};
        const label = t.title || t.artist || file.fileName || '';
        window.electron.analytics.track('navbar_search_select', { type: 'library_track', name: label });
        trackEvent('search_result_clicked', { result_type: 'library_track', name: label });
        // The Library tab scrolls to this track and flash-highlights it — no
        // sticky filter, so the user's view of the rest of the library is
        // untouched. The navbar clears: unlike Mixpedia commits, the "active
        // query" doesn't live on the destination page. No search-history
        // entry: history is a Mixpedia surface shared with mobile, where a
        // local-file entry couldn't resolve to anything.
        dispatch(setLibraryFocusTrack({ filePath: file.filePath || file.file, nonce: Date.now() }));
        dispatch(setActiveTab('visualize'));
        commit('');
    };

    const navigateToMix = (mix) => {
        const q = mix.djName ? `${mix.djName} - ${mix.title}` : mix.title;
        window.electron.analytics.track('navbar_search_select', { type: 'mix', name: q });
        trackEvent('search_result_clicked', { result_type: 'mix', name: q });
        // Id-only navigation via the shared helper (single source of truth
        // with useUnifiedSearch — keep them identical). No title fallback:
        // autocomplete / history mixes always carry an id or a link, and a
        // title search would open a namesake (Mixpedia links by id).
        const filters = buildMixOpenFilters(mix);
        if (!filters) {
            console.debug('[mix open] no id/link on mix, ignoring click', mix?.title);
            return;
        }
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(q));
        dispatch(setActiveTab('djpal'));
        dispatch(searchMixes({ filters }));
        dispatch(addSearchHistoryEntry({
            entityType: 'mix',
            entityPublicId: entityId(mix) || `q:${q}`,
            entityName: mix.title,
            entitySubtitle: mix.djName || null,
            entityArtwork: mix.artworkUrl || null,
            surface: 'mixpedia',
        }));
        commit(q);
    };

    // Event/venue suggestion — destination is the title-filtered PAGINATED
    // mixes list, chip'd with the event name (same landing as the results
    // page's Event top-result card).
    const navigateToEvent = (ev) => {
        const name = ev.name || '';
        if (!name) return;
        trackEvent('search_result_clicked', { result_type: 'event', name, kind: normalizeEventKind(ev.kind) });
        dispatch(setActiveSearch('mix'));
        dispatch(setMixQuery(name));
        dispatch(setActiveTab('djpal'));
        dispatch(setMixFilterContext(eventFilterContext(ev)));
        dispatch(searchMixes({ filters: eventFilters(ev), paginate: true, lite: true }));
        dispatch(addSearchHistoryEntry(eventHistoryEntry(ev)));
        commit(name);
    };

    const navigate = (entry) => {
        if (entry.type === 'all') goToResultsPage(entry.query);
        else if (entry.type === 'library') navigateToLibraryTrack(entry.item);
        else if (entry.type === 'event') navigateToEvent(entry.item);
        else if (entry.type === 'artist') navigateToArtist(entry.item);
        else if (entry.type === 'track') navigateToTrack(entry.item);
        else if (entry.type === 'mix') navigateToMix(entry.item);
        else if (entry.type === 'label') navigateToLabel(entry.item);
    };

    const goToResultsPage = (q) => {
        window.electron.analytics.track('navbar_search', { query: q });
        trackEvent('search_performed', { search_type: 'unified', query: q });
        // openUnifiedResults owns the nav-entry push — a search submitted
        // while already ON Mixpedia gets no setActiveTab push (dedupe), so
        // without it the results page never entered the back stack.
        dispatch(openUnifiedResults(q));
        dispatch(addSearchHistoryEntry({ entityType: 'query', queryText: q, surface: 'mixpedia' }));
        commit(q);
    };

    const reRunHistory = (entry) => {
        if (entry.entityType === 'artist') {
            navigateToArtist({
                name: entry.entityName,
                id: entry.entityPublicId && !entry.entityPublicId.startsWith('name:') ? entry.entityPublicId : undefined,
                artworkUrl: entry.entityArtwork || undefined,
            });
        } else if (entry.entityType === 'track') {
            const title = entry.entityName || '';
            const artist = entry.entitySubtitle || '';
            const isCanonical = entry.entityPublicId && !entry.entityPublicId.startsWith('q:');
            if (isCanonical) {
                // Resolved track entry — go straight to the single-track view.
                navigateToTrack({
                    title,
                    artist,
                    id: entry.entityPublicId,
                    artworkUrl: entry.entityArtwork || undefined,
                });
            } else {
                // Placeholder entry (free-text query like "tal fussman" with no
                // artist) — let runTrackTabSearch route to the list view if the
                // query doesn't resolve as a single track.
                const q = artist ? `${artist} - ${title}` : title;
                if (!q) return;
                window.electron.analytics.track('navbar_search_select', { type: 'track', name: q });
                trackEvent('search_result_clicked', { result_type: 'track', name: q });
                // runTrackTabSearch's synchronous prefix (before its first
                // await) sets the query and routes activeSearch to
                // 'track'/'trackList' — dispatch it BEFORE setActiveTab so the
                // nav marker is typed and the result replaces it (one Back).
                dispatch(runTrackTabSearch({ query: q, artworkUrl: entry.entityArtwork || null }));
                dispatch(setActiveTab('djpal'));
                dispatch(addSearchHistoryEntry({
                    entityType: 'track',
                    entityPublicId: `q:${q}`,
                    entityName: title,
                    entitySubtitle: artist || null,
                    entityArtwork: entry.entityArtwork || null,
                    surface: 'mixpedia',
                }));
                commit(q);
            }
        } else if (entry.entityType === 'event') {
            navigateToEvent(eventFromHistory(entry));
        } else if (entry.entityType === 'label') {
            navigateToLabel({ publicId: entry.entityPublicId, name: entry.entityName || '', artworkUrl: entry.entityArtwork || null });
        } else if (entry.entityType === 'mix') {
            navigateToMix({
                title: entry.entityName || '',
                djName: entry.entitySubtitle || '',
                id: entry.entityPublicId && !entry.entityPublicId.startsWith('q:') ? entry.entityPublicId : undefined,
                artworkUrl: entry.entityArtwork || undefined,
            });
        } else {
            goToResultsPage(entry.queryText || '');
        }
    };

    // Must run on pointerdown, not click: the row activates on pointerdown,
    // so a click-stage stopPropagation fires after the row has already
    // re-run the search.
    const dismissHistory = (e, id) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        dispatch(deleteSearchHistoryEntry(id));
    };

    const clearAllHistory = () => {
        // Navbar's "Clear all" only wipes the Mixpedia surface — Set Planner
        // recents are owned by that tab and shouldn't be wiped from here.
        dispatch(clearSearchHistoryBySurface('mixpedia'));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // If the user picked a recent-search row with arrow keys, re-run it
        if (showHistory && selectedIdx >= 0 && historyItems[selectedIdx]) {
            reRunHistory(historyItems[selectedIdx]);
            return;
        }
        // If the user picked a specific suggestion with arrow keys, go straight there
        if (selectedIdx >= 0 && flat[selectedIdx]) {
            navigate(flat[selectedIdx]);
            return;
        }
        // Otherwise, open the full results page — let the user choose
        const q = query.trim();
        if (q.length >= 2) goToResultsPage(q);
    };

    const handleKeyDown = (e) => {
        // Recent-searches and autocomplete share one selectedIdx — pick the
        // active list so ArrowDown clamps to the right max.
        const navMax = showHistory ? historyItems.length - 1 : flat.length - 1;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIdx((prev) => {
                const next = prev + 1;
                // Wrap to top so the user can keep arrowing forever instead
                // of getting stuck at the bottom.
                return next > navMax ? 0 : next;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIdx((prev) => {
                if (prev <= 0) return navMax;
                return prev - 1;
            });
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };


    // Using onPointerDown instead of onClick so the navigation fires
    // before iOS gets a chance to blur the input → relayout → swallow
    // the click. onClick on iOS fires AFTER blur+layout, which is what
    // caused the "needs 2 taps" autocomplete bug. onPointerDown fires
    // synchronously on touch and is the earliest tap signal we can hook.
    // We only fire the navigation on the primary button to ignore any
    // synthetic right-click / multi-touch noise.
    const handleRowActivate = (action) => (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        e.preventDefault();
        action();
    };

    const renderRow = (entry, globalIdx) => {
        const selected = globalIdx === selectedIdx;
        const key = `${entry.type}-${globalIdx}`;
        if (entry.type === 'all') {
            return (
                <button
                    key={key}
                    className={`navbar-search-item navbar-search-all-row ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => goToResultsPage(entry.query))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    <div className="navbar-search-artwork navbar-search-artwork-placeholder navbar-search-all-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                    </div>
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">
                            See all results for <strong>“{entry.query}”</strong>
                        </span>
                    </div>
                    {/* Keycap hint: advertises the row AND teaches the shortcut. */}
                    <span className="navbar-search-enter-hint">Enter&nbsp;&#9166;</span>
                </button>
            );
        }
        if (entry.type === 'library') {
            const f = entry.item;
            const t = f.track || {};
            return (
                <button
                    key={key}
                    className={`navbar-search-item ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => navigateToLibraryTrack(f))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    {t.cover ? (
                        <CoverArt src={t.cover} size={40} eager className="navbar-search-artwork" style={{ borderRadius: 6 }} />
                    ) : (
                        <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: 6 }}>
                            &#9835;
                        </div>
                    )}
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">{t.title || f.fileName}</span>
                        {t.artist && <span className="navbar-search-genres">{t.artist}</span>}
                    </div>
                </button>
            );
        }
        if (entry.type === 'artist') {
            const a = entry.item;
            const genres = Array.isArray(a.genres) ? a.genres.join(', ') : a.genres;
            return (
                <button
                    key={key}
                    className={`navbar-search-item ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => navigateToArtist(a))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    {a.artworkUrl ? (
                        <CoverArt src={a.artworkUrl} size={40} eager className="navbar-search-artwork" />
                    ) : (
                        <div className="navbar-search-artwork navbar-search-artwork-placeholder">
                            {(a.name || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">{a.name}</span>
                        {genres && <span className="navbar-search-genres">{genres}</span>}
                    </div>
                </button>
            );
        }
        if (entry.type === 'event') {
            const ev = entry.item;
            return (
                <button
                    key={key}
                    className={`navbar-search-item ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => navigateToEvent(ev))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    {ev.covers?.[0] ? (
                        <CoverArt src={ev.covers[0]} size={40} eager className="navbar-search-artwork" style={{ borderRadius: 6 }} />
                    ) : (
                        <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: 6 }}>
                            {(ev.name || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">{ev.name}</span>
                        <span className="navbar-search-genres">{eventKindLabel(ev.kind, ev.type)} · {ev.mixCount} mixes</span>
                    </div>
                </button>
            );
        }
        if (entry.type === 'label') {
            const l = entry.item;
            return (
                <button
                    key={key}
                    className={`navbar-search-item ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => navigateToLabel(l))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    {l.artworkUrl ? (
                        <CoverArt src={l.artworkUrl} size={40} eager className="navbar-search-artwork" style={{ borderRadius: 6 }} />
                    ) : (
                        <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: 6 }}>
                            {(l.name || '?').charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">{l.name}</span>
                        <span className="navbar-search-genres">{l.trackCount ? `Label · ${l.trackCount} tracks` : 'Label'}</span>
                    </div>
                </button>
            );
        }
        if (entry.type === 'track') {
            const t = entry.item;
            return (
                <button
                    key={key}
                    className={`navbar-search-item ${selected ? 'selected' : ''}`}
                    onPointerDown={handleRowActivate(() => navigateToTrack(t))}
                    onMouseEnter={() => setSelectedIdx(globalIdx)}
                >
                    {t.artworkUrl ? (
                        <CoverArt src={t.artworkUrl} size={40} eager className="navbar-search-artwork" style={{ borderRadius: 6 }} />
                    ) : (
                        <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: 6 }}>
                            &#9835;
                        </div>
                    )}
                    <div className="navbar-search-info">
                        <span className="navbar-search-name">{t.title}</span>
                        {t.artist && <span className="navbar-search-genres">{t.artist}</span>}
                    </div>
                </button>
            );
        }
        // mix
        const m = entry.item;
        return (
            <button
                key={key}
                className={`navbar-search-item ${selected ? 'selected' : ''}`}
                onPointerDown={handleRowActivate(() => navigateToMix(m))}
                onMouseEnter={() => setSelectedIdx(globalIdx)}
            >
                {resolveMixCover(m) ? (
                    <CoverArt src={resolveMixCover(m)} size={40} eager className="navbar-search-artwork" style={{ borderRadius: 6 }} />
                ) : (
                    <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: 6 }}>
                        &#9835;
                    </div>
                )}
                <div className="navbar-search-info">
                    <span className="navbar-search-name">{m.title}</span>
                    {m.djName && <span className="navbar-search-genres">{m.djName}</span>}
                </div>
            </button>
        );
    };

    let idx = 0;

    return (
        <div className="navbar-search-outer">
            {/* Mixpedia home — Spotify's home-next-to-search pattern. Replaces
                the per-page × every Mixpedia page used to carry. Hidden on
                mobile (the bottom nav owns navigation there). */}
            {!isMobile && (
                <button
                    type="button"
                    className={`navbar-home-btn ${homeActive ? 'active' : ''}`}
                    title="Mixpedia Home"
                    aria-label="Mixpedia Home"
                    onClick={goMixpediaHome}
                >
                    {/* DESIGN V2: the same house, 23px (20 read small in the 48px button). */}
                    {homeActive ? (
                        <svg width={homeIconPx} height={homeIconPx} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.6l9.5 8.2a1 1 0 01-.65 1.76H19.5V20a1.5 1.5 0 01-1.5 1.5h-3.5V15h-5v6.5H6A1.5 1.5 0 014.5 20v-7.44H3.15a1 1 0 01-.65-1.76L12 2.6z" />
                        </svg>
                    ) : (
                        <svg width={homeIconPx} height={homeIconPx} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 11l9-8 9 8" />
                            <path d="M5.5 9.5V20a1 1 0 001 1H10v-6h4v6h3.5a1 1 0 001-1V9.5" />
                        </svg>
                    )}
                </button>
            )}
            <div ref={wrapperRef} className="navbar-search-wrapper">
            <form onSubmit={handleSubmit}>
                <div className="navbar-search-input-container">
                    <svg className="navbar-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    {isMobile ? (
                        // Mobile uses a plain button as the trigger — a real
                        // <input readOnly> still receives focus on tap and
                        // iOS's focus → blur dance sometimes eats the first
                        // tap and flashes the cursor. A button has no focus
                        // semantics to fight, so the tap is immediate.
                        <button
                            type="button"
                            className="navbar-search-input navbar-search-trigger"
                            onClick={() => setMobileOverlayOpen(true)}
                            aria-label="Open search"
                        >
                            <span className="navbar-search-trigger-placeholder">Search</span>
                        </button>
                    ) : (
                        <input
                            type="search"
                            inputMode="search"
                            enterKeyHint="search"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            value={query}
                            onChange={(e) => handleChange(e.target.value)}
                            onFocus={() => (flat.length > 0 || historyItems.length > 0) && setOpen(true)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search artists, tracks, mixes, library..."
                            className="navbar-search-input"
                        />
                    )}
                    {!isMobile && loading && <div className="navbar-search-spinner" />}
                    {/* Show the clear "x" whenever there's text — independent of
                        `loading`, which can stay stuck (e.g. a hung/unauthed
                        suggestions fetch) and otherwise hides the button entirely. */}
                    {!isMobile && query && (
                        <button
                            type="button"
                            className="navbar-search-clear-btn"
                            // Text affordances edit text: × ONLY clears the
                            // input, never navigates (tried closing the open
                            // page here — yanked users to the home mid-flow).
                            onClick={() => commit()}
                            aria-label="Clear search"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    )}
                </div>
            </form>

            {showHistory && (
                <div
                    className="navbar-search-dropdown"
                    // Stop the input from blurring when a row is tapped.
                    // Without this, iOS blurs the input → keyboard retracts →
                    // viewport relayouts → the row shifts out from under the
                    // finger → first tap misses ("one isn't enough" bug).
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <div className="navbar-search-section-header">Recent searches</div>
                    {historyItems.map((entry, i) => {
                        const isQuery = entry.entityType === 'query';
                        const primary = isQuery ? entry.queryText : entry.entityName;
                        // Placeholder track/mix entries (no entitySubtitle) come
                        // from free-text searches in the Tracks/Mixes tabs and
                        // re-run as list-view searches — pluralize the label so
                        // it reads as "this is a Tracks search" rather than
                        // hinting at a specific track.
                        const secondary = isQuery ? null : (
                            entry.entitySubtitle ||
                            (entry.entityType === 'artist' ? 'Artist'
                             : entry.entityType === 'label' ? 'Label'
                             : entry.entityType === 'track' ? 'Tracks' : 'Mixes')
                        );
                        const fallback = (primary || '?').charAt(0).toUpperCase();
                        const artRadius = entry.entityType === 'artist' ? '50%' : 6;
                        const selected = i === selectedIdx;
                        return (
                            <div
                                key={entry.id}
                                className={`navbar-search-item navbar-search-history-row ${selected ? 'selected' : ''}`}
                                onPointerDown={handleRowActivate(() => reRunHistory(entry))}
                                onMouseEnter={() => setSelectedIdx(i)}
                            >
                                {isQuery ? (
                                    <div className="navbar-search-artwork navbar-search-artwork-placeholder navbar-search-history-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="9" />
                                            <polyline points="12 7 12 12 15 14" />
                                        </svg>
                                    </div>
                                ) : entry.entityArtwork ? (
                                    <CoverArt src={entry.entityArtwork} size={40} eager className="navbar-search-artwork" style={{ borderRadius: artRadius }} />
                                ) : (
                                    <div className="navbar-search-artwork navbar-search-artwork-placeholder" style={{ borderRadius: artRadius }}>
                                        {fallback}
                                    </div>
                                )}
                                <div className="navbar-search-info">
                                    <span className="navbar-search-name">{primary}</span>
                                    {secondary && <span className="navbar-search-genres">{secondary}</span>}
                                </div>
                                <button
                                    type="button"
                                    className="navbar-search-history-dismiss"
                                    onPointerDown={(e) => dismissHistory(e, entry.id)}
                                    aria-label="Remove from recent searches"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        );
                    })}
                    <button
                        type="button"
                        className="navbar-search-history-clear"
                        onClick={clearAllHistory}
                    >
                        Clear recent searches
                    </button>
                </div>
            )}

            {open && !showHistory && flat.length > 0 && (
                <div
                    className="navbar-search-dropdown"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {flat[0]?.type === 'all' &&
                        renderRow(flat[0], idx++)
                    }
                    {groups.library.length > 0 && (
                        <>
                            <div className="navbar-search-section-header">In Your Library</div>
                            {groups.library.map(f => renderRow({ type: 'library', item: f }, idx++))}
                        </>
                    )}
                    {/* Section order MUST come from the same sectionOrder used
                        by buildFlatList or keyboard-nav indexes desync. */}
                    {sectionOrder.map(section => {
                        const items = groups[section] || [];
                        if (!items.length) return null;
                        const header = section === 'events' ? eventSectionHeader(items)
                            : section === 'artists' ? 'Artists'
                                : section === 'labels' ? 'Labels'
                                    : section === 'tracks' ? 'Tracks' : 'Mixes';
                        const type = section === 'events' ? 'event'
                            : section === 'artists' ? 'artist'
                                : section === 'labels' ? 'label'
                                    : section === 'tracks' ? 'track' : 'mix';
                        return (
                            <React.Fragment key={section}>
                                <div className="navbar-search-section-header">{header}</div>
                                {items.map(item => renderRow({ type, item }, idx++))}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            <MobileSearchOverlay
                open={isMobile && mobileOverlayOpen}
                onClose={() => setMobileOverlayOpen(false)}
            />
            </div>
        </div>
    );
};

export default NavbarSearch;
